import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Camera, Check, Loader2, Trash2 } from 'lucide-react'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
} from 'components/ui/dialog'
import { Button } from 'components/ui/button'
import { uploadToCloudinary } from 'components/FileUploader/fileUploadUtils'
import compressImage from 'utils/compressImage'
import { getDeviceId } from 'utils/deviceId'
import { addCaptureImages } from 'slices/captureImageSlice'

export interface CaptureSessionDialogProps {
	open: boolean
	onClose: () => void
	/** Called once a session has been saved, so the library can refresh. */
	onSaved?: () => void
}

interface Shot {
	id: string
	file: File
	previewUrl: string
}

/**
 * One capture session: a label, then as many shots as the job needs.
 *
 * **Why a session rather than a file input.** `capture="environment"` opens the
 * camera for ONE photograph on iOS and then returns to the page. Documenting a
 * space means twenty of them, so the dialog stays open and re-opens the input
 * after each shot. The count is the feedback that the roll is growing.
 *
 * **Why the label comes first.** It is the only thing that makes a roll findable
 * later, and asking for it after twenty photographs is asking someone to name
 * something they have stopped thinking about. It also describes the session
 * rather than any one shot, which is why it is asked once.
 *
 * **Why nothing uploads until Done.** A rep shooting a space is moving, often on
 * a bad connection. Uploading per shot would block the next one behind a
 * request; holding the files and sending them at the end keeps the camera the
 * only thing between the user and the next photograph.
 */
export function CaptureSessionDialog({ open, onClose, onSaved }: CaptureSessionDialogProps) {
	const dispatch = useDispatch()
	const inputRef = useRef<HTMLInputElement>(null)

	const [label, setLabel] = useState('')
	const [shots, setShots] = useState<Shot[]>([])
	const [saving, setSaving] = useState(false)
	const [progress, setProgress] = useState(0)
	const [error, setError] = useState<string | null>(null)

	// Object URLs are held by the browser until revoked, and a session's worth
	// of full-resolution photographs is enough to matter on a phone.
	useEffect(
		() => () => {
			shots.forEach(shot => URL.revokeObjectURL(shot.previewUrl))
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	)

	useEffect(() => {
		if (!open) return
		setLabel('')
		setShots([])
		setSaving(false)
		setProgress(0)
		setError(null)
	}, [open])

	const addFiles = useCallback((files: FileList | null) => {
		if (!files?.length) return
		const added: Shot[] = [...files].map((file, index) => ({
			id: `${Date.now()}-${index}-${file.name}`,
			file,
			previewUrl: URL.createObjectURL(file),
		}))
		setShots(previous => [...previous, ...added])
	}, [])

	const removeShot = useCallback((id: string) => {
		setShots(previous => {
			const shot = previous.find(candidate => candidate.id === id)
			if (shot) URL.revokeObjectURL(shot.previewUrl)
			return previous.filter(candidate => candidate.id !== id)
		})
	}, [])

	const finish = useCallback(async () => {
		if (!label.trim() || shots.length === 0 || saving) return

		setSaving(true)
		setError(null)
		setProgress(0)

		const uploaded: { url: string; uuid?: string; name?: string; size?: number; type?: string }[] = []

		try {
			for (const shot of shots) {
				// 2048px / JPEG 0.8. Twenty untouched phone photographs over LTE
				// is minutes of waiting and a visible drain on the battery.
				const { file } = await compressImage(shot.file)
				const result = await uploadToCloudinary(file, {
					folder: 'mdt/field',
					resourceType: 'image',
				})
				uploaded.push({
					url: result.secure_url,
					uuid: result.public_id,
					name: result.original_filename,
					size: result.bytes,
					type: result.format,
				})
				setProgress(uploaded.length)
			}
		} catch (err: any) {
			// The photographs are still in `shots`, so the user can retry rather
			// than reshoot. Saying which one failed is the difference between
			// that and starting again.
			console.warn('[CaptureSessionDialog] upload failed', err)
			setError(
				`Uploaded ${uploaded.length} of ${shots.length}. Check your signal and try again; nothing has been lost.`,
			)
			setSaving(false)
			return
		}

		const action: any = await (dispatch as any)(
			addCaptureImages({ label: label.trim(), deviceId: getDeviceId(), images: uploaded }),
		)
		setSaving(false)

		// createAsyncThunk RESOLVES on rejectWithValue, so closing here without
		// checking would lose a session and report success.
		if (action?.meta?.requestStatus === 'rejected') {
			setError(String(action.payload ?? 'Could not save these images.'))
			return
		}

		onSaved?.()
		onClose()
	}, [label, shots, saving, dispatch, onSaved, onClose])

	if (!open) return null

	const canFinish = Boolean(label.trim()) && shots.length > 0 && !saving

	return (
		<Dialog open onClose={saving ? () => {} : onClose}>
			<DialogContent className="max-w-sm">
				<DialogHeader>
					<DialogTitle>Capture images</DialogTitle>
					<DialogDescription>
						Name the set, then take as many photos as you need.
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-3 p-5 pt-2">
					<label className="block">
						<span className="text-xs font-medium text-gray-500">Label</span>
						<input
							value={label}
							onChange={e => setLabel(e.target.value)}
							placeholder="e.g. North elevation"
							disabled={saving}
							aria-label="Label"
							className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
							// 16px or larger, or iOS zooms the page on focus.
							style={{ fontSize: '16px' }}
							autoCapitalize="sentences"
						/>
					</label>

					{/*
						One input, re-opened after every shot. `capture` takes a single
						photograph on iOS, so the loop is the dialog staying put rather
						than any multi-shot API.
					*/}
					<input
						ref={inputRef}
						type="file"
						accept="image/*"
						capture="environment"
						multiple
						className="hidden"
						onChange={e => {
							addFiles(e.target.files)
							// Cleared so shooting the same filename twice still fires.
							e.target.value = ''
						}}
					/>

					<Button
						variant="primary"
						isInput
						onClick={() => inputRef.current?.click()}
						disabled={saving}
						className="w-full"
					>
						<Camera className="mr-2 h-4 w-4" />
						{shots.length === 0 ? 'Take a photo' : 'Take another'}
					</Button>

					{shots.length > 0 && (
						<>
							<p className="text-xs text-gray-500">
								{shots.length} {shots.length === 1 ? 'photo' : 'photos'} in this set
							</p>
							<ul className="grid grid-cols-4 gap-1.5">
								{shots.map(shot => (
									<li key={shot.id} className="relative">
										<img
											src={shot.previewUrl}
											alt=""
											className="aspect-square w-full rounded-md object-cover"
										/>
										{!saving && (
											<button
												type="button"
												onClick={() => removeShot(shot.id)}
												aria-label="Remove this photo"
												className="absolute -right-1 -top-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-white text-gray-500 shadow"
											>
												<Trash2 className="h-3 w-3" />
											</button>
										)}
									</li>
								))}
							</ul>
						</>
					)}

					{error && (
						<p role="alert" className="text-sm text-destructive">
							{error}
						</p>
					)}

					<div className="flex gap-2 pt-1">
						<Button variant="ghost" isInput onClick={onClose} disabled={saving} className="flex-1">
							Cancel
						</Button>
						<Button
							variant="primary"
							isInput
							onClick={finish}
							disabled={!canFinish}
							className="flex-1"
						>
							{saving ? (
								<>
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
									{progress}/{shots.length}
								</>
							) : (
								<>
									<Check className="mr-2 h-4 w-4" />
									Done
								</>
							)}
						</Button>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	)
}

export default CaptureSessionDialog
